import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  DEFAULT_LANG,
  LANGUAGES,
  PRIMARY_LANGUAGES,
  getTranslation,
  isSupportedLang,
  makeT,
  resolveInitialLang,
} from "./i18n";
import { PAGE_LANG, writeGoogTransCookie } from "./utils/googleSiteTranslate";

const LangCtx = createContext(null);

const LANG_STORAGE_KEY = "6amico_lang";
const LANG_EXPLICIT_KEY = "6amico_lang_explicit";
const LANG_EVENT = "6amico:langchange";

/** Rileva lingua browser (BCP47 → ISO 639-1). Non usata per il default Home. */
export function detectBrowserLang() {
  if (typeof navigator === "undefined") return DEFAULT_LANG;
  const raw = navigator.language || navigator.userLanguage || DEFAULT_LANG;
  const base = raw.split("-")[0].toLowerCase();
  return isSupportedLang(base) ? base : DEFAULT_LANG;
}

function persistLang(code, { explicit = true } = {}) {
  try {
    if (explicit) localStorage.setItem(LANG_EXPLICIT_KEY, "1");
    localStorage.setItem(LANG_STORAGE_KEY, code);
  } catch {
    /* ignore */
  }
  if (typeof document !== "undefined") {
    document.documentElement.lang = code;
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(LANG_EVENT, { detail: { lang: code } }));
  }
}

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const explicit = localStorage.getItem(LANG_EXPLICIT_KEY) === "1";
      const saved = localStorage.getItem(LANG_STORAGE_KEY);
      const initial = resolveInitialLang({ explicit, saved });
      if (!explicit) persistLang(DEFAULT_LANG, { explicit: false });
      return initial;
    } catch {
      return DEFAULT_LANG;
    }
  });

  const [langEpoch, setLangEpoch] = useState(0);

  const setLang = useCallback((code) => {
    const base = (code || DEFAULT_LANG).split("-")[0].toLowerCase();
    const next = isSupportedLang(base) ? base : DEFAULT_LANG;
    const prev = lang;
    persistLang(next, { explicit: true });
    writeGoogTransCookie(next);
    if (
      typeof document !== "undefined" &&
      next === PAGE_LANG &&
      prev &&
      prev !== PAGE_LANG
    ) {
      const translated =
        document.documentElement.classList.contains("translated-ltr") ||
        document.documentElement.classList.contains("translated-rtl") ||
        (document.body && /translated/.test(document.body.className || ""));
      if (translated) {
        window.setTimeout(() => window.location.reload(), 40);
        return;
      }
    }
    setLangState(next);
    setLangEpoch((n) => n + 1);
  }, [lang]);

  useEffect(() => {
    persistLang(lang, { explicit: localStorage.getItem(LANG_EXPLICIT_KEY) === "1" });
    writeGoogTransCookie(lang);
  }, [lang]);

  const value = useMemo(() => {
    const dict = getTranslation(lang);
    const t = makeT(dict);
    return {
      lang,
      setLang,
      t,
      langEpoch,
      langs: PRIMARY_LANGUAGES,
      langsAll: LANGUAGES,
      pick: (obj) => {
        if (!obj) return "";
        if (typeof obj === "string") return obj;
        if (obj.original && typeof obj.original === "string") return obj.original;
        const code = lang.split("-")[0].toLowerCase();
        if (obj[code] && typeof obj[code] === "string") return obj[code];
        for (const key of ["it", "en", "fr", "de", "es"]) {
          if (obj[key] && typeof obj[key] === "string") return obj[key];
        }
        const first = Object.values(obj).find((v) => typeof v === "string" && String(v).trim());
        return first ? String(first) : "";
      },
      sourceLang: (obj) => {
        if (!obj || typeof obj !== "object") return DEFAULT_LANG;
        return obj._source || DEFAULT_LANG;
      },
    };
  }, [lang, langEpoch, setLang]);

  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

const FALLBACK = {
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: makeT(getTranslation(DEFAULT_LANG)),
  langEpoch: 0,
  langs: PRIMARY_LANGUAGES,
  langsAll: LANGUAGES,
  pick: (obj) => (typeof obj === "string" ? obj : obj?.it || obj?.original || ""),
  sourceLang: () => DEFAULT_LANG,
};

export const useLang = () => useContext(LangCtx) ?? FALLBACK;
